import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import useWalletsApi from '~/composables/api/useWalletsApi';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import type {
  BlockingReason,
  StoreLoadOptions,
  UserInfosType,
  WalletApiResponse,
  FetchError
} from '~/types';

export const useWalletLoader = () => {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;
  const route = useRoute();
  const { notifyApiError } = useApiErrorSnackbar();
  const { getWallet, getWalletByExtId } = useWalletsApi();
  const { userInfos, blockingReasons, walletIntId } = storeToRefs(useWalletsStore());
  const { convertToSearchParams } = useUtils();

  const walletIntIdParam = computed(() =>
    Array.isArray(route.params.id)
      ? parseInt(route.params.id[0], 10)
      : parseInt(route.params.id, 10)
  );

  const wallets = ref<UserInfosType[]>([]);

  const getWalletInfo = async (walletId = walletIntIdParam.value) => {
    try {
      walletIntId.value = undefined;
      const walletData = await getWallet(walletId);
      userInfos.value = walletData.Wallet;
      walletIntId.value = userInfos.value.intId;
      blockingReasons.value = walletData.BlockingReasons
        ? walletData.BlockingReasons.map((reason: BlockingReason) => reason.Id)
        : undefined;

      if (blockingReasons.value) {
        userInfos.value.blockedByPartner = Boolean(
          blockingReasons.value.find((reason) => reason === 3)
        );
        userInfos.value.blockedByLemonway = Boolean(
          blockingReasons.value.find((reason) => reason !== 3)
        );
      }
    } catch (error: unknown) {
      const responseStatus = (error as FetchError)?.response?.status;
      if (responseStatus === 404) {
        navigateTo({
          name: 'partner-wallets-all',
          params: { partner: route.params.partner }
        });
        notifyApiError(error, { title: t('common.idNotFound', { id: route.params.id }) as string });
        return;
      }
      notifyApiError(error);
    }
  };

  const getWallets = async (filter: Array<Array<string> | string>) => {
    try {
      const queryObject: StoreLoadOptions = { skip: 0, take: 10 };
      if (filter.length > 0) {
        queryObject.filter = JSON.stringify(filter);
      }
      const searchParams = convertToSearchParams(queryObject);
      const response: WalletApiResponse = await getWalletByExtId(searchParams.toString());
      wallets.value = response.data;
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  return { walletIntIdParam, wallets, getWalletInfo, getWallets };
};
